import { keepPreviousData, queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import type { ModerationMark } from '@/lib/moderation';
import { api } from './client';
import type { ConversationChannel, ConversationStatus, Page } from './types';
import type { WhatsappTemplate } from './whatsapp-templates';

/**
 * Cliente de la bandeja (espejo de `apps/api/src/schemas/inbox.ts`): `GET|POST /admin/tenants/:tenantId/inbox*`.
 * Tiempo casi real SIN polling agresivo: la lista, el resumen y la conversación abierta se refrescan cada 15 s solo con la pestaña visible.
 * El texto de los mensajes lleva datos personales: nunca se guarda fuera de la caché de Query (nada en localStorage ni en la URL).
 */

export const INBOX_REFRESH_MS = 15_000;

export const INBOX_STATES = ['handoff', 'pending', 'mine', 'open', 'closed', 'all'] as const;
export type InboxState = (typeof INBOX_STATES)[number];
export type InboxChannel = Extract<ConversationChannel, 'widget' | 'whatsapp' | 'instagram'>;

export interface InboxWindow {
  open: boolean;
  expiresAt: string | null;
  remainingSeconds: number;
}

export interface InboxAssignee {
  id: string;
  name: string;
}

export interface InboxItem {
  id: string;
  channel: ConversationChannel;
  status: ConversationStatus;
  agentPaused: boolean;
  assignedTo: InboxAssignee | null;
  assignedToMe: boolean;
  contact: { name: string; phoneMasked: string | null } | null;
  startedAt: string;
  lastMessageAt: string;
  unread: number;
  preview: string | null;
  window: InboxWindow | null;
  /** Marca de moderación (autolesión, violencia…); sin el texto del mensaje. */
  moderation?: ModerationMark | null;
}

export interface InboxSummary {
  pending: number;
  mine: number;
  handoff: number;
  unread: number;
}

export type ReplyBlockedReason = 'not_taken' | 'taken_by_other' | 'window_closed' | 'closed' | 'channel_unsupported';

export interface InboxMessage {
  id: string;
  role: 'user' | 'assistant' | 'human';
  text: string;
  at: string;
  author: { id: string | null; name: string } | null;
  delivery: { status: 'queued' | 'sent' | 'delivered' | 'read' | 'failed'; errorCode: string | null } | null;
}

export interface InboxMedia {
  id: string;
  kind: 'image' | 'audio' | 'video' | 'document';
  status: 'stored' | 'rejected';
  rejectReason: string | null;
  mime: string | null;
  bytes: number;
  createdAt: string;
  url: string | null;
  messageId: string | null;
  retainedUntil: string;
  linkExpiresAt: string | null;
}

export interface InboxDetail extends Omit<InboxItem, 'contact'> {
  agentName: string;
  contact: { name: string; phone: string | null; email: string | null } | null;
  messages: InboxMessage[];
  truncated: boolean;
  media: InboxMedia[];
  canReply: boolean;
  replyBlockedReason: ReplyBlockedReason | null;
  /** WhatsApp: plantillas aprobadas de la cuenta que responde. */
  approvedTemplates: number;
  /** Ventana cerrada + conversación tomada por mí + al menos una plantilla aprobada. */
  canSendTemplate: boolean;
}

export interface InboxFilters {
  state: InboxState;
  channel?: InboxChannel | undefined;
  unread?: boolean | undefined;
}

export interface InboxTakeResult {
  conversationId: string;
  status: ConversationStatus;
  assignedTo: InboxAssignee | null;
  changed: boolean;
}

export interface InboxSendResult {
  messageId: string;
  created: boolean;
  delivery: 'queued' | 'delivered';
}

const root = (tenantId: string) => ['tenants', tenantId, 'inbox'] as const;

export const inboxListQuery = (tenantId: string, f: InboxFilters, cursor?: string) =>
  queryOptions({
    queryKey: [...root(tenantId), 'list', f.state, f.channel ?? null, f.unread ?? false, cursor ?? null],
    queryFn: ({ signal }) =>
      api.get<Page<InboxItem>>(
        `/admin/tenants/${tenantId}/inbox`,
        { state: f.state, channel: f.channel, unread: f.unread ? 'true' : undefined, cursor, limit: 25 },
        signal,
      ),
    refetchInterval: INBOX_REFRESH_MS,
    refetchIntervalInBackground: false,
    // Mientras llega la página nueva se conserva la anterior DEL MISMO tenant (la clave incluye el tenant: nunca se mezclan).
    placeholderData: keepPreviousData,
  });

export const inboxSummaryQuery = (tenantId: string) =>
  queryOptions({
    queryKey: [...root(tenantId), 'summary'],
    queryFn: ({ signal }) => api.get<InboxSummary>(`/admin/tenants/${tenantId}/inbox/summary`, undefined, signal),
    refetchInterval: INBOX_REFRESH_MS,
    refetchIntervalInBackground: false,
  });

export const inboxDetailQuery = (tenantId: string, conversationId: string) =>
  queryOptions({
    queryKey: [...root(tenantId), 'detail', conversationId],
    queryFn: ({ signal }) =>
      api.get<InboxDetail>(`/admin/tenants/${tenantId}/inbox/${conversationId}`, undefined, signal),
    refetchInterval: INBOX_REFRESH_MS,
    refetchIntervalInBackground: false,
    // Cada lectura queda auditada (una por usuario y hora): no se vuelve a pedir al volver a la pestaña, solo por el intervalo.
    refetchOnWindowFocus: false,
  });

/** Tras una acción se refresca todo lo de la bandeja de ESTE tenant (lista, resumen y conversaciones). */
function useInvalidateInbox(tenantId: string) {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: root(tenantId) });
}

export function useTakeConversation(tenantId: string) {
  const invalidate = useInvalidateInbox(tenantId);
  return useMutation({
    mutationFn: (conversationId: string) =>
      api.post<InboxTakeResult>(`/admin/tenants/${tenantId}/inbox/${conversationId}/take`),
    onSettled: invalidate,
  });
}

export function useReleaseConversation(tenantId: string) {
  const invalidate = useInvalidateInbox(tenantId);
  return useMutation({
    mutationFn: (conversationId: string) =>
      api.post<InboxTakeResult>(`/admin/tenants/${tenantId}/inbox/${conversationId}/release`),
    onSettled: invalidate,
  });
}

export function useMarkInboxRead(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (conversationId: string) =>
      api.post<{ ok: true }>(`/admin/tenants/${tenantId}/inbox/${conversationId}/read`),
    onSuccess: () => {
      // Solo la lista y el resumen: refrescar la conversación abierta volvería a leerla (y a auditarla).
      void qc.invalidateQueries({ queryKey: [...root(tenantId), 'list'] });
      void qc.invalidateQueries({ queryKey: [...root(tenantId), 'summary'] });
    },
  });
}

/** Texto libre o plantilla aprobada (con sus valores). */
export type InboxSendInput =
  | { text: string; clientMessageId: string }
  | {
      template: {
        templateId: string;
        header?: string | undefined;
        headerMediaUrl?: string | undefined;
        body?: string[] | undefined;
        buttonUrls?: string[] | undefined;
      };
      clientMessageId: string;
    };

export interface InboxTemplates {
  accountAvailable: boolean;
  templates: WhatsappTemplate[];
}

/** Plantillas aprobadas con las que se puede responder a esta conversación (solo se pide con la ventana cerrada). */
export const inboxTemplatesQuery = (tenantId: string, conversationId: string) =>
  queryOptions({
    queryKey: [...root(tenantId), 'templates', conversationId],
    queryFn: ({ signal }) =>
      api.get<InboxTemplates>(`/admin/tenants/${tenantId}/inbox/${conversationId}/templates`, undefined, signal),
    staleTime: 60_000,
  });

export function useSendHumanMessage(tenantId: string, conversationId: string) {
  const invalidate = useInvalidateInbox(tenantId);
  return useMutation({
    mutationFn: (input: InboxSendInput) =>
      api.post<InboxSendResult>(`/admin/tenants/${tenantId}/inbox/${conversationId}/messages`, input),
    onSettled: invalidate,
    // El texto es un dato personal: no se conserva en la caché de mutaciones.
    gcTime: 0,
  });
}
